'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import FooterClientWrapper from '@/hotdesk-ui/components/footer/FooterClientWrapper';
import AppInitializer from './app-initializer';
import { ToastV4Container, toast } from './components/toast-v4';
import DarkNavbarClientWrapper from './components/dark-nav-bar/DarkNavbarClientWrapper';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { fetchRemoteConfig, firebaseAnalytics } from './common-classes/firebase.service';
import { RemoteConfigKeys } from './enum/remote-keys';
import Image from 'next/image';
import ChatwootWidget from './services/ChatwootWidget';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from './store/store';
import { isTokenExpired } from './utils/tokenUtils';
import { logout, setRefreshToken, setToken } from './store/slices/auth.slice';
import { ApiConfigration } from './common-classes/api-configuration';
import { AuthService } from './services/auth.service';
import dynamic from 'next/dynamic';
import ErrorModalClient from './components/error-modal/ErrorModalClient';
import LocationPopupClient from './components/location-popup/LocationPopupClient';
import IpGeoBootstrap from './components/IpGeoBootstrap';
import { tracking } from './services/tracking.service';
import { getCompareConfig } from './lib/compare/config';

// Own chunk: the tracker (+ assist) is only downloaded when it is enabled.
const OpenReplay = dynamic(() => import('./services/session-tracking.service'), {
  ssr: false
});
const getAuthService = async (languageCode: string): Promise<AuthService> => {
  const apiConfig = await new ApiConfigration().getApiConfig(languageCode);
  return new AuthService(apiConfig);
};
const LayoutClientWrapper = ({
  children,
  languageCode,
  isDirectory,
  selfUrl,
  ipCountryCode
}: Readonly<{
  children: React.ReactNode;
  languageCode: string;
  isDirectory: boolean;
  selfUrl: string;
  ipCountryCode?: string;
}>) => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations();
  const token = useSelector((state: RootState) => state.auth.token);
  const refreshToken = useSelector((state: RootState) => state.auth.refresh);
  const dispatch = useDispatch();
  const [whatsAppLink, setWhatsAppLink] = useState('');
  const [showWhatsAppButton, setShowWhatsAppButton] = useState(false);
  const [chatWood, setChatWood] = useState<boolean>(false);
  const [openReplay, setOpenReplay] = useState<boolean>(false);
  const [itidaTextFieldEnabled, setItidaTextFieldEnabled] = useState(false);
  const matchesDynamicRoute = (pathname: string, route: string) => {
    const routeRegex = new RegExp(`^${route.replace(/:[^\s/]+/g, '([^/]+)')}$`, 'u');
    return routeRegex.test(pathname);
  };
  const noFixedComponentsRoutes = ['/request-visit', '/request-visit/tell-us', '/request-visit/success', '/feedback', '/delete-account', '/long-term-quote', '/business-form'];
  const showFixedComponents = !noFixedComponentsRoutes.some(route => matchesDynamicRoute(pathname, route));

  // View transitions preserve scroll position across client navigations (e.g. footer links
  // from long workspace/listing pages). Reset to top whenever the route changes.
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        left: 0
      });
    };
    scrollToTop();
    const doc = document as Document & {
      activeViewTransition?: {
        finished: Promise<void>;
      };
    };
    if (doc.activeViewTransition) {
      void doc.activeViewTransition.finished.then(scrollToTop);
    }
  }, [pathname]);
  const whatsAppClicked = async () => {
    firebaseAnalytics('whatsapp_button_clicked');
  };
  const getRemoteConfig = async () => {
    try {
      const whatsAppLink = await fetchRemoteConfig(RemoteConfigKeys.WEB_WHATSAPP_LINK, 'string');
      setWhatsAppLink(whatsAppLink as string);
      const showWhatsApp = await fetchRemoteConfig(RemoteConfigKeys.WEB_SHOW_WHATSAPP_BUTTON, 'boolean');
      setShowWhatsAppButton(!!showWhatsApp);
      const chatWoodConfig = await fetchRemoteConfig(RemoteConfigKeys.WEB_CHATWOOT_SWITCH, 'boolean');
      if (selfUrl === 'https://www.hotdesk.com/') {
        setChatWood(chatWoodConfig as boolean);
        // The old hard-coded project key 404s on api.openreplay.com, so
        // only run when a valid key is configured for the deploy.
        setOpenReplay(!!process.env.NEXT_PUBLIC_OPENREPLAY_PROJECT_KEY);
      } else {
        setChatWood(true);
        setOpenReplay(false);
      }
      const itidaTextField = await fetchRemoteConfig(RemoteConfigKeys.WEB_LINKED_ACCOUNTS_ITIDA_TEXT_FIELD, 'boolean');
      setItidaTextFieldEnabled(itidaTextField as boolean);
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    getRemoteConfig();
  }, [languageCode]);

  // compare_flag on every event (PRD § Tracking): whether Compare is on for
  // this user, so the KPIs can be compared with and without it.
  useEffect(() => {
    getCompareConfig().then(({
      enabled
    }) => tracking.addUserVariable('compare_flag', enabled ? 'on' : 'off'));
  }, []);
  useEffect(() => {
    const isItida = searchParams?.get('itida') === 'true';
    const isAuthenticated = !!token && !isTokenExpired(token);
    if (isItida && isAuthenticated && itidaTextFieldEnabled) {
      if (pathname === '/my-account') {
        const currentParams = new URLSearchParams(searchParams.toString());
        currentParams.set('tab', 'linked-accounts');
        router.replace(`/my-account?${currentParams.toString()}`);
      } else {
        router.replace('/my-account?tab=linked-accounts&itida=true');
      }
    }
  }, [searchParams, token, pathname, router, itidaTextFieldEnabled]);
  useEffect(() => {
    if (typeof window !== 'undefined' && itidaTextFieldEnabled) {
      const showItidaToast = sessionStorage.getItem('show_itida_toast');
      if (showItidaToast === 'true') {
        sessionStorage.removeItem('show_itida_toast');
        setTimeout(() => {
          toast.success(<div>
                            {t('my-account.linked-accounts.itida-success-toast')}
                            <span onClick={e => {
              e.stopPropagation();
              router.push('/memberships');
            }} style={{
              textDecoration: 'underline',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}>
                                {t('my-account.linked-accounts.itida-success-toast-link')}
                            </span>
                            {t('my-account.linked-accounts.itida-success-toast-end')}
                        </div>, {
            position: 'bottom-center'
          });
        }, 500);
      }
    }
  }, [pathname, t, router, itidaTextFieldEnabled]);
  useEffect(() => {
    if (!!token && isTokenExpired(token)) {
      if (!!refreshToken) {
        getAuthService(languageCode).then(async service => {
          await service.getUser({
            token: token,
            refresh: refreshToken
          }).then(response => {
            if (!!response.user) {
              const newToken = response.token;
              const newRefreshToken = response.refresh;
              dispatch(setToken(newToken));
              dispatch(setRefreshToken(newRefreshToken));
            } else {
              console.error('Failed to refresh token');
              dispatch(logout());
            }
          }).catch(error => {
            console.error('Error fetching user:', error);
            dispatch(logout());
          });
        }).catch(error => {
          console.error('Error during token refresh:', error);
          dispatch(logout());
        });
      } else {
        console.error('Failed to refresh token');
        dispatch(logout());
      }
    }
  }, [token]);
  return <>
            <IpGeoBootstrap ipCountryCode={ipCountryCode} data-sentry-element="IpGeoBootstrap" data-sentry-source-file="LayoutclientWrapper.tsx" />
            {openReplay && <OpenReplay />}
            <AppInitializer data-sentry-element="AppInitializer" data-sentry-source-file="LayoutclientWrapper.tsx" />
            {showFixedComponents && <DarkNavbarClientWrapper languageCode={languageCode} isDirectory={isDirectory} isBannerVisible={false} style="navbarDark" />}
            {showFixedComponents && <LocationPopupClient />}
            {/* Header is an 82px bar plus a 12px spacer (see Navbar.module.scss).
                On the routes where the header also carries the mobile search
                band, the Navbar measures itself and overrides the variable. */}
            <main style={{
      paddingTop: showFixedComponents ? 'var(--fixed-header-height, 94px)' : 0
    }}>
                {children}
            </main>
            <ToastV4Container rtl={languageCode === 'ar'} data-sentry-element="ToastV4Container" data-sentry-source-file="LayoutclientWrapper.tsx" />
            {!!chatWood && <ChatwootWidget />}
            {showWhatsAppButton && whatsAppLink !== '' && <a aria-label="Chat on WhatsApp" href={whatsAppLink} className="fixed-icon" onClick={() => whatsAppClicked()} onDragStart={e => e.preventDefault()} draggable={false} target="_blank">
                    <Image width={20} height={21} alt="Chat on WhatsApp" src="/assets/icons/whatsapp-white.svg" draggable={false} />
                    <span className="d-none d-md-inline-block bold">
                        {t('WhatsApp')}
                    </span>
                </a>}

            {showFixedComponents && <>
                    <FooterClientWrapper languageCode={languageCode} />
                </>}
            <ErrorModalClient show={false} data-sentry-element="ErrorModalClient" data-sentry-source-file="LayoutclientWrapper.tsx" />
        </>;
};
export default LayoutClientWrapper;